import { Facebook, Instagram, MapPin, Phone } from "lucide-react";
import logoAsset from "@/assets/oz-waste-logo.webp.asset.json";
const logo = logoAsset.url;
import { googleBusinessUrl } from "@/data/reviews";

/**
 * Simple TikTok brand icon (lucide-react does not ship brand icons).
 * Stroke weight and viewBox are tuned to match the surrounding Lucide icons.
 */
const TikTokIcon = ({ className, strokeWidth = 1.8 }: { className?: string; strokeWidth?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4c.5 1.5 2 3 4 3" />
  </svg>
);

/**
 * Official OZ WASTE CO. social profile URLs.
 * Edit these here to update the footer social icons.
 * The Google Business Profile URL is sourced from @/data/reviews
 * (single source of truth, shared with the hero trust badge).
 */
const socialLinks = [
  { label: "Visit OZ WASTE CO. on Facebook", href: "https://www.facebook.com/profile.php?id=61588732268747&sk=directory_contact_info", Icon: Facebook },
  { label: "Visit OZ WASTE CO. on Instagram", href: "https://www.instagram.com/ozmove.co/", Icon: Instagram },
  { label: "Visit OZ WASTE CO. on TikTok", href: "https://www.tiktok.com/@oz.move.co", Icon: TikTokIcon },
  { label: "View OZ WASTE CO. on Google Maps", href: googleBusinessUrl, Icon: MapPin },
];

const footerLinks = {
  Services: [
    { label: "Household Rubbish", href: "/household-rubbish-removal" },
    { label: "Furniture Removal", href: "/furniture-removal" },
    { label: "Move-Out Clear-Outs", href: "/move-out-clear-outs" },
    { label: "Garage & Shed Clear-Outs", href: "/garage-shed-clear-outs" },
    { label: "Office & Commercial", href: "/commercial-rubbish-removal" },
    { label: "Property Clear-Outs", href: "/property-clear-outs" },
    { label: "Green Waste Removal", href: "/green-waste-removal" },
  ],

  Locations: [
    { label: "Melbourne", href: "/melbourne" },
    { label: "Sydney", href: "/sydney" },
    { label: "Brisbane", href: "/brisbane" },
    { label: "Perth", href: "/perth" },
    { label: "Adelaide", href: "/adelaide" },
    { label: "Gold Coast", href: "/gold-coast" },
    { label: "All Locations", href: "/locations" },
  ],
  Company: [
    { label: "How It Works", href: "/#how-it-works" },
    { label: "Reviews", href: "/#reviews" },
    { label: "Get a Quote", href: "/#quote" },
    { label: "Why Choose Us", href: "/#why-choose-us" },
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Blog", href: "/blog" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-conditions" },
  ],
};

const Footer = () => (
  <footer className="relative bg-[#111111] pt-14 pb-8">
    <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#FDC00E] via-orange to-[#FDC00E]" />
    <div className="container">
      {/* Top row - logo, tagline, phone */}
      <div className="mb-10">
        <img src={logo} alt="OZ WASTE CO." className="h-28 md:h-32 w-auto mb-3" width="384" height="256" loading="lazy" decoding="async" />
        <p className="text-[#FDC00E] font-bold text-base">Fast. Reliable. Gone.</p>
        <p className="text-white/60 text-xs mt-1.5">Rubbish removal without the hassle.</p>


        <div className="mt-5">
          <p className="text-white/65 text-[11px] uppercase tracking-wider font-semibold mb-1.5">Call us</p>
          <a
            href="tel:1800556654"
          data-call-source="footer"
            className="flex items-center gap-2 text-[#FDC00E] font-bold text-xl hover:text-orange transition-colors w-fit"
          >
            <Phone className="w-5 h-5" />
            1800 556 654
          </a>
        </div>
      </div>

      {/* Link columns */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-10">
        {Object.entries(footerLinks).map(([heading, links]) => (
          <div key={heading}>
            <p className="font-bold text-white text-sm mb-3.5">{heading}</p>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/45 text-sm hover:text-orange transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Social icons */}
      <div className="mb-8 flex justify-center md:justify-start gap-3">
        {socialLinks.map(({ label, href, Icon }, index) => (
          <a
            key={label}
            href={href}
            aria-label={label}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-white/50 transition-all duration-200 hover:-translate-y-0.5 hover:border-orange hover:text-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange/60 ${
              index === socialLinks.length - 1 ? "ml-2 md:ml-3" : ""
            }`}
          >
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </a>
        ))}
      </div>

      {/* Divider + copyright + business details */}
      <div className="border-t border-white/10" />
      <div className="pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-white/60 text-xs text-center md:text-left">
        <p>© 2026 OZ WASTE CO. Pty Ltd. All rights reserved.</p>
        <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-4 md:justify-end">
          <span>Melbourne, Victoria</span>
          <a
            href="tel:1800556654"
            data-call-source="footer_business_details"
            aria-label="Call 1800 556 654"
            className="hover:text-orange transition-colors"
          >
            1800 556 654
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
